import React from 'react';
import { Paper, PaperProps, Box, BoxProps } from '@mui/material';

interface CardProps extends PaperProps {
  children: React.ReactNode;
  hover?: boolean;
  gradient?: string;
  padding?: BoxProps['padding'];
}

/**
 * Carte moderne avec effets de hover et glassmorphism
 */
export const Card: React.FC<CardProps> = ({
  children,
  hover = false,
  gradient,
  padding = 3,
  sx,
  ...props
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: gradient || 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(18, 155, 175, 0.1)',
        '&:hover': hover
          ? {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(18, 155, 175, 0.15)',
            }
          : {},
        ...sx,
      }}
      {...props}
    >
      <Box sx={{ padding }}>{children}</Box>
    </Paper>
  );
};

/**
 * Carte de statistique avec valeur et titre
 */
export interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: number;
  color?: string;
  sx?: BoxProps['sx'];
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  color = '#129baf',
  sx,
}) => {
  return (
    <Card
      hover
      sx={{
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon && <Box sx={{ color, mr: 2 }}>{icon}</Box>}
        {trend !== undefined && (
          <Box
            sx={{
              ml: 'auto',
              color: trend >= 0 ? '#2ecc71' : '#e74c3c',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </Box>
        )}
      </Box>
      <Box sx={{ fontSize: '1.75rem', fontWeight: 700, color, mb: 0.5 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Box>
      <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
        {title}
      </Box>
    </Card>
  );
};

export default Card;
